import { refreshSession } from "@/services/api";
import { authSnapshot } from "@/store/auth";
import { useLocaleStore } from "@/store/locale";

/**
 * Reading an endpoint that answers while it works.
 *
 * `fetch`, not the shared axios client: axios cannot hand back a response body
 * as it arrives in a browser, and `EventSource` only ever sends a GET, which is
 * no use for something whose input is a file. The cost is the interceptors —
 * the headers below are theirs, spelled out, and so is the retry on a 401.
 */
export type Frame = { event: string; data: string; id?: string };

const frame = (block: string): Frame => {
  let event = "message";
  let id: string | undefined;
  const data: string[] = [];
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    // Read, not ignored: this is what a reader sends back to resume where it
    // stopped, and dropping it here would make every reconnect a fresh start
    // with the changes in between lost.
    else if (line.startsWith("id:")) id = line.slice(3).trim();
    // The colon-space after the field name is optional in the format, and a
    // data line may be one of several making up a single payload.
    else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
  }
  return { event, data: data.join("\n"), ...(id === undefined ? {} : { id }) };
};

/**
 * Send, then read the event stream that comes back, frame by frame.
 *
 * The shared middle of both readers below: the one retry on a stale token, the
 * refusal to start when the answer is not a stream, and the framing. ``send``
 * is given the token to use, because the retry has to send the *same* request
 * again with a new one.
 *
 * Throws before yielding anything when the answer is not a stream — an
 * unreadable file is still a 422 and a clash with another run still a 409, and
 * those carry a problem+json the caller shows as it would any other error.
 */
async function* readFrames(
  send: (token: string | null) => Promise<Response>,
): AsyncGenerator<Frame> {
  let answer = await send(authSnapshot().accessToken);
  // The same second chance every other request gets, which this one was
  // missing: an access token lasts fifteen minutes and a person choosing
  // columns for a two-thousand-row file can easily spend that, so the token
  // that was fine when the dialog opened is stale by the time they submit.
  // The refresh is the shared one — rotating it here as well as in the
  // interceptor would look like a replayed token, and the server answers that
  // by revoking every session.
  //
  // Retried once. A second 401 is a session that is genuinely gone, and asking
  // again would only spend another refresh token to be told so.
  if (answer.status === 401) {
    try {
      const tokens = await refreshSession();
      answer = await send(tokens.access_token);
    } catch {
      // Nothing to refresh with, or the refresh was refused. The 401 below is
      // the honest answer, and the interceptor's own failure path has already
      // signed the person out.
    }
  }
  if (!answer.ok || !answer.body) {
    const problem: unknown = await answer.json().catch(() => null);
    throw new StreamError(answer.status, problem);
  }

  const reader = answer.body.pipeThrough(new TextDecoderStream()).getReader();
  let held = "";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      held += value;
      // A read is a slice of the stream, not a frame: one can carry several and
      // one frame can span two, so only what is terminated is handed on.
      let split = held.indexOf("\n\n");
      while (split !== -1) {
        const block = held.slice(0, split);
        held = held.slice(split + 2);
        if (block.trim()) yield frame(block);
        split = held.indexOf("\n\n");
      }
    }
  } finally {
    await reader.cancel().catch(() => undefined);
  }
}

/**
 * POST a body and read the event stream that comes back.
 *
 * For an endpoint whose input is a file, which `EventSource` could never send:
 * it only ever makes a GET.
 */
export function postEventStream(
  url: string,
  body: BodyInit,
  signal?: AbortSignal,
): AsyncGenerator<Frame> {
  return readFrames((token) =>
    fetch(url, {
      method: "POST",
      body,
      ...(signal ? { signal } : {}),
      headers: {
        // No Content-Type: the browser writes the multipart boundary, and
        // naming the type ourselves would leave it out.
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Accept-Language": useLocaleStore.getState().locale,
        Accept: "text/event-stream",
      },
    }),
  );
}

/**
 * Hold a GET open and read what arrives on it.
 *
 * `fetch` rather than `EventSource` for one reason: `EventSource` cannot set a
 * header, so the token would have to go in the query string, where it lands in
 * proxy logs and `Referer`. The price is that reconnecting is ours to do —
 * `EventSource` does it for free — which is why the caller is a hook and not a
 * bare call.
 *
 * `lastEventId` travels as a query parameter, not the `Last-Event-ID` header it
 * ought to be. The preview gateway answers the CORS preflight itself with a
 * fixed allow-list that does not name that header, so a browser there is
 * refused before the request is made. The backend reads either.
 */
export function getEventStream(
  url: string,
  { signal, lastEventId }: { signal?: AbortSignal; lastEventId?: string | null } = {},
): AsyncGenerator<Frame> {
  const target = new URL(url, window.location.origin);
  if (lastEventId) target.searchParams.set("last_event_id", lastEventId);
  return readFrames((token) =>
    fetch(target.toString(), {
      method: "GET",
      ...(signal ? { signal } : {}),
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Accept-Language": useLocaleStore.getState().locale,
        Accept: "text/event-stream",
      },
    }),
  );
}

/** A stream that never started, carrying whatever the server said instead. */
export class StreamError extends Error {
  status: number;
  problem: unknown;
  constructor(status: number, problem: unknown) {
    super(`stream failed with ${status}`);
    this.status = status;
    this.problem = problem;
  }
}
